(() => {
if (window.__productPersonalisationLoaded) return;
window.__productPersonalisationLoaded = true;

let openHelpTrigger = null;
let helpIsPinned = false;

const getHelpPopover = (trigger) => {
  const id = trigger?.getAttribute('aria-controls');
  return id ? document.getElementById(id) : null;
};

const openHelp = (trigger, pinned = false) => {
  if (!trigger) return;

  if (openHelpTrigger && openHelpTrigger !== trigger) {
    closeHelp();
  }

  const popover = getHelpPopover(trigger);
  if (!popover) return;

  openHelpTrigger = trigger;
  helpIsPinned = pinned;
  trigger.setAttribute('aria-expanded', 'true');
  popover.hidden = false;
};

const closeHelp = (returnFocus = false) => {
  if (!openHelpTrigger) return;

  const trigger = openHelpTrigger;
  const popover = getHelpPopover(trigger);

  trigger.setAttribute('aria-expanded', 'false');

  if (popover) {
    popover.hidden = true;
  }

  openHelpTrigger = null;
  helpIsPinned = false;

  if (returnFocus) {
    trigger.focus();
  }
};

class ProductPersonalisation {
  constructor(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    this.root = root;
    this.form = root.closest('form');
    this.intentionSelect = root.querySelector('[data-intention-select]');
    this.customIntention = root.querySelector('[data-custom-intention]');
    this.customContainer = root.querySelector('[data-custom-intention-container]');
    this.giftToggle = root.querySelector('[data-gift-toggle]');
    this.giftFields = root.querySelector('[data-gift-fields]');
    this.giftMessage = root.querySelector('[data-gift-message]');
    this.giftStatus = root.querySelector('[data-gift-status]');
    this.nameInput = root.querySelector('[data-name-input]');
    this.birthdateInput = root.querySelector('[data-birthdate-input]');
    this.nameLabel = root.querySelector('[data-name-label]');
    this.birthdateLabel = root.querySelector('[data-birthdate-label]');
    this.errors = root.querySelector('[data-personalisation-errors]');
    this.intentionSelect?.addEventListener('change', () => this.updateIntention());
    this.giftToggle?.addEventListener('change', () => this.updateGift());
    this.form?.addEventListener('invalid', (event) => this.showInvalid(event), true);
    this.form?.addEventListener('input', (event) => this.clearInvalid(event));
    this.updateIntention();
    this.updateGift();
  }

  updateIntention() {
    if (!this.intentionSelect || !this.customIntention) return;
    const custom = this.intentionSelect.value === 'Custom';
    this.customContainer.hidden = !custom;
    this.customIntention.disabled = !custom;
    this.customIntention.required = custom;
    if (!custom) this.customIntention.value = '';
  }

  updateGift() {
    if (!this.giftToggle) return;
    const gift = this.giftToggle.checked;
    this.giftToggle.setAttribute('aria-expanded', String(gift));
    this.giftFields.hidden = !gift;
    this.giftMessage.disabled = !gift;
    this.giftStatus.disabled = !gift;
    if (!gift) this.giftMessage.value = '';
    if (this.nameInput) this.nameInput.name = gift ? 'properties[Recipient Name]' : 'properties[Name]';
    if (this.birthdateInput) this.birthdateInput.name = gift ? 'properties[Recipient Birthdate]' : 'properties[Birthdate]';
    this.updateLabel(this.nameLabel, gift);
    this.updateLabel(this.birthdateLabel, gift);
  }

  updateLabel(label, useRecipientLabel) {
    if (!label) return;
    const labelText = label.querySelector('[data-label-text]');
    if (!labelText) return;
    labelText.textContent = useRecipientLabel ? label.dataset.recipientLabel || '' : label.dataset.selfLabel || '';
  }

  showInvalid(event) {
    if (!this.root.contains(event.target)) return;
    event.target.setAttribute('aria-invalid', 'true');
    this.errors.textContent = 'Please complete the highlighted required fields.';
    this.errors.hidden = false;
  }

  clearInvalid(event) {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement)) return;
    if (event.target.checkValidity()) event.target.removeAttribute('aria-invalid');
    if (!this.root.querySelector('[aria-invalid="true"]')) this.errors.hidden = true;
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(
    '[data-personalisation-help-trigger]'
  );

  if (trigger) {
    event.preventDefault();

    if (openHelpTrigger === trigger && helpIsPinned) {
      closeHelp();
    } else {
      openHelp(trigger, true);
    }

    return;
  }

  if (!event.target.closest('[data-personalisation-help]')) {
    closeHelp();
  }
});

document.addEventListener('pointerover', (event) => {
  if (
    !window.matchMedia('(hover: hover)').matches ||
    !(event.target instanceof Element)
  ) {
    return;
  }

  const help = event.target.closest('[data-personalisation-help]');

  if (
    !help ||
    (
      event.relatedTarget instanceof Node &&
      help.contains(event.relatedTarget)
    )
  ) {
    return;
  }

  const trigger = help.querySelector(
    '[data-personalisation-help-trigger]'
  );

  if (!helpIsPinned) {
    openHelp(trigger);
  }
});

document.addEventListener('pointerout', (event) => {
  if (
    !window.matchMedia('(hover: hover)').matches ||
    !(event.target instanceof Element)
  ) {
    return;
  }

  const help = event.target.closest('[data-personalisation-help]');

  if (
    !help ||
    (
      event.relatedTarget instanceof Node &&
      help.contains(event.relatedTarget)
    )
  ) {
    return;
  }

  if (!helpIsPinned) {
    closeHelp();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeHelp(true);
  }
});

const initializePersonalisation = (scope = document) => {
  if (scope instanceof Element && scope.matches('[data-product-personalisation]')) new ProductPersonalisation(scope);
  scope.querySelectorAll('[data-product-personalisation]').forEach((root) => new ProductPersonalisation(root));
};

initializePersonalisation();
document.addEventListener('shopify:section:load', (event) => initializePersonalisation(event.target));
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) if (node instanceof Element) initializePersonalisation(node);
  }
}).observe(document.documentElement, { childList: true, subtree: true });
})();
